
import React from 'react';
import { Calendar, PlaneTakeoff } from 'lucide-react';
import { 
  Card, 
  CardContent,
  CardHeader,
  CardTitle 
} from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Trip, ItineraryEvent } from '@/types/itinerary';
import DailySidebar from '@/components/itinerary/DailySidebar';
import { groupEventsByDate } from './ItineraryUtils';
import { formatDate } from '@/utils/dateUtils';
import { useTransportation } from '@/hooks/travel/useTransportation';
import AddTransportationDialog from '@/components/travel/AddTransportationDialog';
import { useIsMobile } from '@/hooks/use-mobile';

import TimelineTab from './tabs/TimelineTab';
import ListView from './tabs/ListView';
import TravelTab from './tabs/TravelTab';

interface ItineraryContentProps {
  trip: Trip | null;
  events: ItineraryEvent[];
  selectedDate: string;
  tripDays: string[];
  canEditEvents: boolean;
  onSelectDate: (date: string) => void;
  onEditEvent: (event: ItineraryEvent) => void;
  onDeleteEvent: (eventId: string) => void;
  onAddEvent: () => void;
  setEvents: (events: ItineraryEvent[]) => void;
}

const ItineraryContent: React.FC<ItineraryContentProps> = ({
  trip,
  events,
  selectedDate,
  tripDays,
  canEditEvents,
  onSelectDate,
  onEditEvent,
  onDeleteEvent,
  onAddEvent,
  setEvents
}) => {
  const grouped = groupEventsByDate(events);
  const isMobile = useIsMobile();
  
  const dates = tripDays.length > 0 ? tripDays : Object.keys(grouped).sort();
  const eventsForSelectedDate = grouped[selectedDate] || [];
  
  const {
    transportationEvents,
    isLoading: isLoadingTransportation,
    error: transportationError,
    isAddDialogOpen,
    outboundDetails,
    returnDetails,
    handleOutboundChange,
    handleReturnChange,
    saveOutboundDetails,
    saveReturnDetails,
    fetchTransportationEvents,
    retryLoading,
    openAddTransportationDialog,
    closeAddTransportationDialog
  } = useTransportation(trip, canEditEvents);
  
  console.log("In ItineraryContent - Selected date:", selectedDate);
  console.log("In ItineraryContent - Available dates:", dates);
  
  const handleDateSelect = (date: string) => {
    console.log(`Date ${date} selected in ItineraryContent, current selection: ${selectedDate}`);
    onSelectDate(date);
  };
  
  // Format the date for display, with a shortened version for mobile
  const getFormattedDate = () => {
    if (!selectedDate) return 'All Events';
    
    if (isMobile) {
      // Use a shortened date format for mobile
      try {
        const fullDate = formatDate(selectedDate);
        const parts = fullDate.split(',');
        if (parts.length >= 2) {
          // Only show day and date (e.g., "Thu, Nov 16")
          return `${parts[0].substring(0, 3)}, ${parts[1].trim()}`;
        }
        return fullDate;
      } catch (e) {
        return formatDate(selectedDate);
      }
    }
    
    return formatDate(selectedDate);
  };
  
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-4 w-full max-w-full">
      <div className="lg:col-span-1 w-full">
        <DailySidebar 
          dates={dates} 
          selectedDate={selectedDate}
          onSelectDate={handleDateSelect}
          events={events}
        />
      </div>
      
      <div className="lg:col-span-3 w-full overflow-hidden">
        <Card className="w-full overflow-hidden">
          <CardHeader className="pb-3">
            <div className="flex items-center">
              <Calendar className="h-5 w-5 mr-2 text-glowingTeal shrink-0" />
              <CardTitle className="text-xl truncate">
                {getFormattedDate()}
              </CardTitle>
            </div>
          </CardHeader>
          
          <CardContent className="overflow-auto pb-6">
            <Tabs defaultValue="timeline" className="w-full">
              <TabsList className="bg-muted/50 mb-4 w-full grid grid-cols-3 gap-1">
                <TabsTrigger 
                  value="timeline" 
                  className="data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs sm:text-sm px-1 sm:px-3"
                >
                  {isMobile ? "Me" : "My Timeline"}
                </TabsTrigger>
                <TabsTrigger 
                  value="list"
                  className="data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs sm:text-sm px-1 sm:px-3"
                >
                  {isMobile ? "Group" : "Group Timeline"}
                </TabsTrigger>
                <TabsTrigger 
                  value="travel"
                  className="data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs sm:text-sm px-1 sm:px-3"
                >
                  <PlaneTakeoff className="h-3.5 w-3.5 mr-1 sm:mr-2" />
                  {isMobile ? "Travel" : "All Travel"}
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="timeline" className="mt-0">
                <TimelineTab
                  date={selectedDate}
                  events={eventsForSelectedDate}
                  canEdit={canEditEvents}
                  onEdit={onEditEvent}
                  onDelete={onDeleteEvent}
                  onAddEvent={onAddEvent}
                  setEvents={setEvents}
                  allEvents={events}
                />
              </TabsContent>
              
              <TabsContent value="list" className="mt-0">
                <ListView events={eventsForSelectedDate} />
              </TabsContent>
              
              <TabsContent value="travel" className="mt-0">
                <TravelTab
                  transportationEvents={transportationEvents}
                  selectedDate={selectedDate}
                  isLoading={isLoadingTransportation}
                  error={transportationError}
                  canEdit={canEditEvents}
                  onRefresh={fetchTransportationEvents}
                  onRetry={retryLoading}
                  onAddTransportation={openAddTransportationDialog}
                />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
      
      {canEditEvents && (
        <AddTransportationDialog
          open={isAddDialogOpen}
          onClose={closeAddTransportationDialog}
          outboundDetails={outboundDetails}
          returnDetails={returnDetails}
          onOutboundChange={handleOutboundChange}
          onReturnChange={handleReturnChange}
          onSaveOutbound={saveOutboundDetails}
          onSaveReturn={saveReturnDetails}
        />
      )}
    </div>
  );
};

export default ItineraryContent;
